import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  DollarSign, ArrowUpRight, ArrowDownLeft, ChevronRight, RefreshCw,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Sessão UI-1 — RecentTransactionsCard
//
// 5 últimas transações com hierarquia clara: ícone direcional (saída/entrada)
// tonalizado + título + categoria/data + valor monospace.
//
// FIN-1.1 (security): /api/finance/transactions agora filtra por user_id
// no backend via JWT. Cliente envia Authorization Bearer. Filtro
// client-side por user.id é redundância defensiva — preservado por ora,
// mas pode ser removido em sessão de housekeeping.

const fmtBRL = (v, sign) => {
  const abs = Math.abs(Number(v) || 0);
  const formatted = abs.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  return sign === "-" ? `−${formatted}` : `+${formatted}`;
};

function formatTxDate(dateStr) {
  if (!dateStr) return "";
  try {
    const d = dateStr.includes("T") ? parseISO(dateStr) : parseISO(`${dateStr}T00:00:00`);
    return format(d, "dd MMM", { locale: ptBR });
  } catch { return dateStr; }
}

export default function RecentTransactionsCard({ onSeeAllClick }) {
  const { user, token } = useAuth();
  const userId = user?.id;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setError(null);
    try {
      // FIN-1.1: backend filtra por user_id via JWT. limit=20 é folga
      // confortável (era 200 quando havia filtro client-side).
      const r = await fetch(`${API_URL}/api/finance/transactions?limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      const all = Array.isArray(data?.transactions) ? data.transactions : [];
      // Filtro client-side residual por defesa em profundidade.
      const mine = userId
        ? all.filter(t => !t.userId || t.userId === userId || t.user_id === userId)
        : all;
      setItems(mine.slice(0, 5));
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, [userId, token]);

  useEffect(() => { load(); }, [load]);

  const Header = () => (
    <div className="flex items-center justify-between mb-5">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-brand-green/10 flex items-center justify-center">
          <DollarSign className="w-4 h-4 text-brand-green" aria-hidden="true" />
        </div>
        <div className="flex flex-col">
          <span
            className="text-caption font-semibold uppercase text-brand-green/80"
            style={{ letterSpacing: "0.12em" }}
          >
            Extrato
          </span>
          <span className="text-micro text-foreground-subtle">Últimas transações</span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="p-1.5 rounded-md text-foreground-muted hover:text-brand-green hover:bg-brand-green/10 transition-colors disabled:opacity-50"
          aria-label="Atualizar extrato"
          data-testid="extrato-refresh"
        >
          <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
        </button>
        {onSeeAllClick && (
          <button
            type="button"
            onClick={onSeeAllClick}
            className="inline-flex items-center gap-1 text-caption text-foreground-muted hover:text-brand-green transition-colors"
            data-testid="extrato-see-all"
          >
            Ver todas
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <Card variant="elevated" className="glass-card p-6 md:p-7" data-testid="recent-transactions-card">
        <Header />
        <div className="space-y-2">
          {[0, 1, 2, 3, 4].map(i => (
            <div key={i} className="h-12 rounded-lg bg-surface-card-hover/40 animate-pulse" />
          ))}
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card variant="elevated" className="glass-card p-6 md:p-7" data-testid="recent-transactions-card">
        <Header />
        <p className="text-caption text-status-error">Não foi possível carregar transações.</p>
      </Card>
    );
  }

  if (items.length === 0) {
    return (
      <Card variant="elevated" className="glass-card p-6 md:p-7" data-testid="recent-transactions-card">
        <Header />
        <div className="flex flex-col items-start gap-3 py-6">
          <div className="w-12 h-12 rounded-full bg-brand-green/10 flex items-center justify-center">
            <DollarSign className="w-5 h-5 text-brand-green" aria-hidden="true" />
          </div>
          <h3 className="font-display font-semibold text-foreground text-h3">
            Nenhuma transação ainda
          </h3>
          <p className="text-caption text-foreground-muted">
            Registre sua primeira transação em Financeiro.
          </p>
          {onSeeAllClick && (
            <Button variant="secondary" size="md" onClick={onSeeAllClick} className="mt-1" data-testid="extrato-empty-cta">
              <DollarSign className="w-4 h-4" />
              Ir para Financeiro
            </Button>
          )}
        </div>
      </Card>
    );
  }

  return (
    <Card variant="elevated" className="glass-card p-6 md:p-7" data-testid="recent-transactions-card">
      <Header />
      <ul className="divide-y divide-white/5" data-testid="extrato-list">
        <AnimatePresence>
          {items.map((tx, idx) => {
            const isExpense = tx.type === "expense";
            const sign = isExpense ? "-" : "+";
            const Icon = isExpense ? ArrowUpRight : ArrowDownLeft;
            const iconBg = isExpense ? "bg-status-error/10 text-status-error" : "bg-brand-green/10 text-brand-green";
            const valueColor = isExpense ? "text-status-error" : "text-brand-green";
            const title = (tx.description || "(sem descrição)").toUpperCase();
            const dateLabel = formatTxDate(tx.date || tx.created_at);
            return (
              <motion.li
                key={tx.id || idx}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.03, duration: 0.2 }}
                className="flex items-center gap-4 py-3"
              >
                <div className={cn("w-10 h-10 rounded-md flex items-center justify-center shrink-0", iconBg)}>
                  <Icon className="w-4 h-4" aria-hidden="true" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate" style={{ letterSpacing: "0.02em" }}>
                    {title}
                  </p>
                  <div className="mt-0.5 flex items-center gap-2 text-micro text-foreground-subtle">
                    {dateLabel && <span>{dateLabel}</span>}
                    {tx.category && (
                      <Badge variant="outline" className="capitalize">{tx.category}</Badge>
                    )}
                  </div>
                </div>
                <div className={cn("font-mono font-bold tabular-nums text-sm md:text-base shrink-0", valueColor)}>
                  {fmtBRL(tx.amount, sign)}
                </div>
              </motion.li>
            );
          })}
        </AnimatePresence>
      </ul>
    </Card>
  );
}
